/**
 * WidgetSidebar — barre latérale 240px, 9 widgets en pleine hauteur.
 *
 * PHASE 3.0 UX Revolution (DECISIONS #167).
 * PHASE 3.1 : W2-W5 onClick → navigate /welcome?focus=module:xxx&level=2.
 * V3-LAYOUT  : 9 widgets toujours visibles, flex-1 égal, séparateur fin SMART/CLASSIQUE.
 *              Toggle Smart/Classique supprimé.
 */

import { useMarqueCliente } from '@/lib/marque-cliente';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  ShoppingCart,
  TrendingUp,
  Shield,
  FlaskConical,
  Globe,
  Map,
  Swords,
  Rocket,
} from 'lucide-react';
import { SmartWidget } from '@/components/shared/SmartWidget';
import { ClassicWidget } from '@/components/shared/ClassicWidget';
import { FlashDirectWidget } from '@/components/shared/FlashDirectWidget';
import { useAlertsDrillStore } from '@/stores/alertsDrillStore';
import {
  usePerfWidget,
  useDqWidget,
} from '@/app/hooks/useWidgetSnapshots';
import { useDaV2NationalSeries } from '@/modules/data-accuracy-v2/hooks/queries';
import { useMarcheKpiCards } from '@/modules/marche/hooks/useMarcheKpiCards';
import { usePhenomenaActive } from '@/modules/phenomena/hooks/usePhenomenaActive';
import { useGpsDispersion } from '@/modules/performance/hooks/useGpsDispersion';
import { useGpsBeginEndScatter } from '@/modules/performance/hooks/useGpsBeginEnd';
import type { IhsGlobalData } from '@/modules/marche/types/marche.types';
import { ROUTES } from '@/lib/routes';
import { useT } from '@/lib/i18n';
import { useOverviewBrandCode } from '@/stores/overviewBrandStore';

// ── Types ──────────────────────────────────────────────────────────────────────

interface WidgetSidebarProps {
  sidebarW: number;
}

// ── Section label ──────────────────────────────────────────────────────────────


// ── WidgetSidebar ──────────────────────────────────────────────────────────────

export const WIDGET_SIDEBAR_W = 240; // px — exporté pour AppShell + TopNav

export function WidgetSidebar({ sidebarW }: WidgetSidebarProps) {
  const navigate       = useNavigate();
  const { pathname }   = useLocation();
  const focusedAlertId = useAlertsDrillStore((s) => s.focusedAlertId);
  // `#796` i18n — les noms de modules viennent du catalogue partagé (invariants
  //   rendus identiques par le `uppercase` CSS ; FR/EN pour les autres).
  const t              = useT();
  const marqueCliente  = useMarqueCliente();

  // ── Dimming : un seul widget SMART actif à la fois sur /alerts ────────────
  const onAlertsPage   = pathname === ROUTES.welcome || pathname.startsWith(ROUTES.welcome + '/');
  const hasModuleFocus = onAlertsPage && !!focusedAlertId?.startsWith('module:');

  // ── Données widgets ────────────────────────────────────────────────────────
  // Cadran RETAIL figé sur la dernière semaine — il ne suit PAS les filtres du
  // module (choix Hkm). Les 3 params sont donc explicites et volontairement
  // constants.
  //
  // ⚠ `channel_type` DOIT être passé : le backend le défaut à 'all'
  // (market/api/schemas.ts:68), et le widget affichait Retail + Wholesale
  // confondus alors que la page Marché montre le Retail seul — d'où deux
  // chiffres différents pour le même KPI. Idem `brand_view` : la page l'envoie
  // selon son toggle, l'omettre ici retombait sur le défaut 'group'.
  //
  // NB : ces params ne coïncident pas avec ceux de la page Marché (qui porte en
  // plus `grain`), donc pas de partage de cache — la query key est
  // ['market', <kpi>, params]. C'est le prix d'un cadran figé.
  const marcheKpis = useMarcheKpiCards({
    period_preset: 'latest_week',
    channel_type:  'retail',
    brand_view:    'group',
  });

  // #340 D-CHANTIER3-REFONDRE-P2 (22/07) — le widget MARKET OVERVIEW suit le
  // sélecteur marque local (overviewBrandStore, partagé UNIQUEMENT avec
  // /phenomena/marche). Défaut null = Marché total = Samsung POV (comportement V1
  // preservé via `.find(r=>r.isClientBrand)`). Marque tierce (ex REALME) =
  // extraction `.find(r=>r.dimValue===brandCode)` sur les mêmes payloads
  // multi-marques (useMarcheKpiCards renvoie déjà TOUTES les marques).
  const overviewBrand = useOverviewBrandCode();
  const brandMatcher = overviewBrand
    ? (r: { dimValue: string; isClientBrand: boolean }) => r.dimValue.toUpperCase() === overviewBrand.toUpperCase()
    : (r: { dimValue: string; isClientBrand: boolean }) => r.isClientBrand;

  const brandVol = marcheKpis.pdmVolume.data?.data.visibleRows.find(brandMatcher);
  const brandVal = marcheKpis.pdmValue.data?.data.visibleRows.find(brandMatcher);
  const ihsData    = marcheKpis.ihs.data?.data;
  // IHS : le global renvoie ratio Samsung POV. En mode marque tierce, on lit
  // depuis le breakdown 'brand' quand disponible ; sinon fallback global (—
  // affiché tel quel pour ne pas mentir sur la marque).
  const ihsGlobal  = ihsData && 'breakdown' in ihsData && ihsData.breakdown === 'global'
    ? (ihsData as IhsGlobalData)
    : null;

  const pdmVolPct = brandVol != null ? `${brandVol.pct.toFixed(1)} %` : '—';
  const pdmValPct = brandVal != null ? `${brandVal.pct.toFixed(1)} %` : '—';
  // IHS marque tierce non exposé par ce cadran figé (useMarcheKpiCards.ihs renvoie
  // le global Samsung POV). Pour éviter d'afficher la valeur Samsung sous un label
  // marque tierce, on masque en mode overview brand ≠ null.
  const ihsPct    = overviewBrand
    ? '—'
    : (ihsGlobal?.ihsRatioPct != null ? `${ihsGlobal.ihsRatioPct.toFixed(1)} %` : '—');

  // W1 — MX RM FLOW : comptes phénomènes toutes marques, tous canaux
  const phenQ      = usePhenomenaActive();
  const phenData   = phenQ.data?.data ?? [];
  const countMajeur = phenData.filter((p) => p.ampleur === 'MAJEUR').length;
  const countModere = phenData.filter((p) => p.ampleur === 'MODERE').length;
  const countMineur = phenData.filter((p) => p.ampleur === 'MINEUR').length;

  const perfQ     = usePerfWidget();
  const visitRate = perfQ.data?.data as { visitRatePct?: number | null; visitsPlanned?: number } | undefined;

  // D-GPS-SEUILS-VERIFIES-CODE (2026-07-19) — commentaire précédent PÉRIMÉ :
  // annonçait CONFORME_SEUIL_M=100 pour la conformité begin↔end ; le fix UX du
  // 17/07 (cf G3-DEUX-SEUILS-AMBIGUS) l'a changé à 50 sans mise à jour de ce
  // commentaire — même famille de bug que celui qu'il décrivait. Valeurs
  // RE-VÉRIFIÉES en code au 19/07 :
  //   - Widget KPI 2 « Dispersion ≤ 50 m » = `pctSub50` = bucket '0' SEUL de
  //     GpsDispersionChart = % begin↔PDV ≤ 50 m ("conforme fin"). DÉLIBÉRÉMENT
  //     plus strict que le chiffre « Conforme » (≤ 100 m, bucket 0+1) affiché
  //     en tête de GpsDispersionChart lui-même — décision Hkm 19/07 : signal
  //     STRICT au widget (aperçu exigeant), définition complète réservée au
  //     chart. Le libellé porte désormais le seuil pour lever l'ambiguïté :
  //     ce n'est plus "Dispersion" nu, mais "Dispersion ≤ 50 m" explicite.
  //   - Widget KPI 3 « Conformité » = `pctConforme` de GpsBeginEndScatterResult,
  //     `seuilM = CONFORME_SEUIL_M = 50` (src/modules/performance/engine/
  //     builders/gps-begin-end.ts:28) — même seuil que le cadran G3 complet.
  //
  // ⚠ `'retail'` explicite — sans lui le backend défaute à pdv_type='all' et
  // n'applique AUCUN filtre canal : les 2 équipes étaient moyennées. Les
  // sections du module (GpsDispersionChart / GpsBeginEndScatter) gardent leur
  // comportement : le canal est un paramètre du hook, pas une constante.
  const gpsDispQ    = useGpsDispersion(null, 'retail');
  const gpsBeginEnd = useGpsBeginEndScatter('retail');
  const bucket0     = gpsDispQ.data?.buckets?.find(b => b.key === '0');
  const nPoints     = gpsDispQ.data?.nPoints ?? 0;
  const pctSub50    = bucket0 && nPoints > 0 ? (bucket0.n / nPoints) * 100 : null;
  const pctConfBE   = gpsBeginEnd.data?.pctConforme ?? null;
  const fmtPct      = (v: number | null, dec = 0): string => v == null ? '—' : `${v.toFixed(dec)} %`;

  const dqQ         = useDqWidget();

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isActive = (path: string) =>
    pathname === path || pathname.startsWith(path + '/');

  function handleMarketClick() {
    // Fix destinations widgets (2026-07-26) — W6 « MX RM FLOW » pointe désormais
    // vers le module Marché unifié (ROUTES.marche = MarcheRetailPage, section
    // MxRmFlowSection + S1-S7). Avant : marcheHome (page Phénomènes complète)
    // — la cible de W1 qui pointait à tort vers phenomenaMarche (version amputée).
    navigate(ROUTES.marche);
  }
  return (
    <motion.aside
      data-testid="widget-sidebar"
      initial={false}
      animate={{ left: sidebarW }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className="fixed top-0 z-30 flex flex-col border-r border-surface-dark-border bg-surface-dark"
      style={{ width: WIDGET_SIDEBAR_W, height: '100vh' }}
      aria-label={t('commun.widgetsNavigation')}
    >
      {/* ── Brand header — aligne avec la TopNav (h-14 = 56px) ─────────────── */}
      <div className="flex h-14 shrink-0 items-center justify-center border-b border-surface-dark-border px-3">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex flex-col items-center gap-0.5 cursor-pointer bg-transparent border-0 p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-samsung-500/50 rounded-sm"
          aria-label={t('commun.accueilDashboard')}
        >
          {/* ⚠⚠ `#839` — **DEUX RÈGLES DIFFÉRENTES, CÔTE À CÔTE.**
              *Le premier est le nom du CLIENT : il se **lit** (`#815`), il ne
              se peint pas — la clé existe pour ça, pas pour traduire (FR == EN).
              Le second est le nom du PRODUIT : c'est un **invariant `#800`**,
              il reste littéral. Le témoin d'absence connaît les deux et dit
              pourquoi il les laisse passer.* */}
          <span className="text-[11px] font-semibold tracking-widest uppercase text-muted-foreground/60 leading-none">
            {t('commun.marqueClientePays', { marque: marqueCliente.nom })}
          </span>
          <span className="text-sm font-bold tracking-tight leading-none text-brand-samsung-500">
            MX SFMT Dashboard
          </span>
        </button>
      </div>

      {/* ── Widgets (100% de l'espace restant, 9 cartes flex-1 égales) ─────── */}
      {/* Spacer flex-1 = 100vh - 56px ; inner div = h-full → couvre tout, plus de vide en bas */}
      <div className="flex-1 min-h-0">
      {/* ⚠ `#1093` — LE RAIL DÉFILE QUAND LA HAUTEUR MANQUE. *Les cartes gardaient une
          hauteur proportionnelle (`flex-1`) et TRANCHAIENT leur contenu : sous ~745 px de
          viewport utile, les sous-libellés étaient coupés (mesuré : boîte 57 px, contenu
          70 px quand le titre passe sur deux lignes). Chaque carte a désormais un PLANCHER
          égal à son contenu, et c'est la COLONNE qui défile — jamais une carte qui rogne. */}
      <div className="flex flex-col h-full px-2 pb-2 pt-2 gap-[9px] overflow-y-auto overflow-x-hidden">
      {/* W1 — Marché */}
      <div className="flex-1 min-h-[4.5rem]">
        <SmartWidget
          id="market"
          icon={<AlertTriangle />}
          label={t('nav.phenomenes')}
          value={phenQ.data ? String(countMajeur) : '—'}
          subLabel={t('ampleur.majeur')}
          kpi2={{ value: phenQ.data ? String(countModere) : '—', label: t('ampleur.modere') }}
          kpi3={{ value: phenQ.data ? String(countMineur) : '—', label: t('ampleur.mineur') }}
          isLoading={false}
          // Vague dettes 2 (2026-07-27) — les deux pages ont fusionné.
          // W1 « MARKET PHENOMENA » pointe désormais vers /phenomena/marche
          // (PhenomenaMarketPage porte désormais les contrôles OverviewBrandSelector
          // + OverviewChannelToggle qui vivaient sur l'ex /marche/home).
          isActive={isActive(ROUTES.phenomenaMarche)}
          isDimmed={hasModuleFocus}
          onClick={() => navigate(ROUTES.phenomenaMarche)}
        />
      </div>

      {/* W2 — Performance */}
      <div className="flex-1 min-h-[4.5rem]">
        <SmartWidget
          id="perf"
          icon={<TrendingUp />}
          label={t('nav.sfmPerf')}
          value={
            visitRate?.visitRatePct != null
              ? `${visitRate.visitRatePct.toFixed(1)} %`
              : '—'
          }
          subLabel={t('widget.sfm.tauxVisite')}
          kpi2={{ value: fmtPct(pctSub50,  1), label: t('widget.sfm.dispersion') }}
          kpi3={{ value: fmtPct(pctConfBE, 1), label: t('widget.sfm.conformite') }}
          isLoading={perfQ.isLoading || gpsDispQ.isLoading || gpsBeginEnd.isLoading}
          isActive={isActive(ROUTES.performance)}
          isDimmed={hasModuleFocus}
          onClick={() => navigate(ROUTES.performance)}
        />
      </div>

      {/* W4 — Data Accuracy V2 (D-DATA-ACCURACY-V2-P2 #363).
          Le widget affiche désormais les DEUX SCORES du module (Quality + Proof)
          et leur écart. La file des signaux actionnables reste accessible sur
          l'onglet Anomalies. */}
      <div className="flex-1 min-h-[4.5rem]">
        <DaV2Widget
          dqSummaryWeekIso={dqQ.data?.weekIso ?? null}
          isActive={isActive(ROUTES.dataQuality)}
          isDimmed={hasModuleFocus}
          onNavigate={() => navigate(ROUTES.dataQuality)}
        />
      </div>

      {/* W6 — Market Overview KPIs (PDM Vol / PDM Val / IHS) */}
      <div className="flex-1 min-h-[4.5rem]">
        <SmartWidget
          id="market-rw"
          icon={<ShoppingCart />}
          label={overviewBrand ? `MX RM FLOW · ${overviewBrand.toUpperCase()}` : 'MX RM FLOW'}
          value={pdmVolPct}
          subLabel={t('widget.mxrm.pdmVol')}
          kpi2={{ value: pdmValPct, label: t('commun.pdmValAbbr') }}
          kpi3={{ value: ihsPct,    label: t('widget.mxrm.ihs') }}
          isLoading={marcheKpis.isAnyLoading}
          // Fix destinations widgets (2026-07-26) — W6 pointe vers ROUTES.marche
          // (module Marché unifié, MxRmFlowSection). isActive aligné.
          isActive={isActive(ROUTES.marche)}
          isDimmed={hasModuleFocus}
          onClick={handleMarketClick}
        />
      </div>

      {/* W8 — Geo-data (affiché en 6e position depuis le renommage UI) */}
      <div className="flex-1 min-h-[3.75rem]">
        <ClassicWidget
          id="geo"
          icon={<Globe />}
          label={t('nav.geoData')}
          subLabel={t('nav.geoData.sous')}
          to={ROUTES.geoData}
          isActive={isActive(ROUTES.geoData)}
        />
      </div>

      {/* W5 — Flash Info (accès direct, affiché en 7e position) */}
      <div className="flex-1 min-h-[3.75rem]">
        <FlashDirectWidget
          isActive={isActive(ROUTES.flashinfoFeed) || pathname.startsWith('/flashinfo')}
        />
      </div>

      {/* Brand vs Brand — placeholder (au-dessus de SKU BENCHMARKING) */}
      <div className="flex-1 min-h-[3.75rem]">
        <ClassicWidget
          id="brand-vs-brand"
          icon={<Swords />}
          label={t('nav.brandVsBrand')}
          subLabel={t('nav.brandVsBrand.sous')}
          to={ROUTES.brandVsBrand}
          isActive={isActive(ROUTES.brandVsBrand)}
        />
      </div>

      {/* S10 — Lancements officiels. Module AUTONOME : un suivi de lancement se
          consulte pendant les mois qui suivent une sortie, pas chaque semaine.
          ⚠ Ne mène PAS à du vide : Samsung n'a aucun lancement, l'état vide de la
          page est véridique et porte le sélecteur pour aller voir les concurrents. */}
      <div className="flex-1 min-h-[3.75rem]">
        <ClassicWidget
          id="official-launches"
          icon={<Rocket />}
          label={t('nav.cohabitation')}
          subLabel={t('nav.cohabitation.sous')}
          to={ROUTES.officialLaunches}
          isActive={isActive(ROUTES.officialLaunches)}
        />
      </div>

      {/* W9 — Comparateur SKU (affiché en 8e position depuis le renommage UI) */}
      <div className="flex-1 min-h-[3.75rem]">
        <ClassicWidget
          id="sku"
          icon={<Map />}
          label={t('nav.comparateur')}
          subLabel={t('nav.comparateur.sous')}
          to={ROUTES.comparator}
          isActive={isActive(ROUTES.comparator)}
        />
      </div>

      {/* W7 — Self Service Lab (affiché en 9e position) */}
      <div className="flex-1 min-h-[3.75rem]">
        <ClassicWidget
          id="lab"
          icon={<FlaskConical />}
          label={t('nav.lab')}
          subLabel={t('nav.lab.sous')}
          to={ROUTES.lab}
          isActive={isActive(ROUTES.lab)}
        />
      </div>

      </div>
      </div>
    </motion.aside>
  );
}

// ── W4 Data Accuracy V2 — sous-composant (2 scores + écart) ─────────────────
// Utilise dqSummaryWeekIso comme ancre. Fenêtre = LA DERNIÈRE SEMAINE seule, sans libellé
// (décision Hkm 14/09/2026, validée écran — le passage depuis 17 sem. date de 0ba1dbb, 27/07).
interface DaV2WidgetProps {
  dqSummaryWeekIso: string | null;
  isActive: boolean;
  isDimmed: boolean;
  onNavigate: () => void;
}
export function DaV2Widget({ dqSummaryWeekIso, isActive, isDimmed, onNavigate }: DaV2WidgetProps) {
  const t = useT();
  const weekMax = dqSummaryWeekIso ?? '';
  // Dernière semaine uniquement — weekMin = weekMax
  const q = useDaV2NationalSeries(weekMax, weekMax);
  const series = q.data?.series ?? [];

  const { qualityPct, proofPct, gapPts } = (() => {
    const last = series[series.length - 1];
    if (!last) return { qualityPct: null as number | null, proofPct: null as number | null, gapPts: null as number | null };
    const qp = last.qualityEvaluable > 0 ? last.qualityPass / last.qualityEvaluable : 0;
    const pp = last.visits > 0 ? last.proven / last.visits : 0;
    return { qualityPct: qp, proofPct: pp, gapPts: (qp - pp) * 100 };
  })();

  const fmtPct = (n: number | null, dec = 0) => n == null ? '—' : `${(n * 100).toFixed(dec)}%`;
  const fmtGap = (n: number | null) => n == null ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(1)}`;

  return (
    <div data-testid="widget-sidebar-da-v2" className="h-full">
      <SmartWidget
        id="dq"
        icon={<Shield />}
        label={t('nav.dataAccuracy')}
        value={fmtPct(qualityPct, 1)}
        subLabel={t('widget.da.qualite')}
        kpi2={{ value: fmtPct(proofPct, 1), label: t('widget.da.preuves') }}
        kpi3={{ value: fmtGap(gapPts), label: t('widget.da.ecartQP') }}
        isLoading={q.isLoading}
        isActive={isActive}
        isDimmed={isDimmed}
        onClick={onNavigate}
      />
    </div>
  );
}
